import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Award, Download, Calendar, FileText, Loader2, CheckCircle2,
  Building2, AlertCircle
} from 'lucide-react';
import { doadorApiCall } from './DoadorPortalLayout';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

export default function DoadorCertificadosPage() {
  const [loading, setLoading] = useState(true);
  const [certificados, setCertificados] = useState([]);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear().toString());
  const [gerando, setGerando] = useState(null);

  useEffect(() => {
    loadCertificados();
  }, []);

  const loadCertificados = async () => {
    try {
      const data = await doadorApiCall('/doador/certificados');
      setCertificados(data || []);
    } catch (error) {
      console.error('Erro ao carregar certificados:', error);
    } finally {
      setLoading(false);
    }
  };

  const gerarCertificadoAnual = async () => {
    setGerando('anual');
    try {
      const data = await doadorApiCall(`/doador/certificados/anual/${anoSelecionado}`);
      if (data.erro) {
        toast.error(data.erro);
        return;
      }
      
      // Gerar PDF
      gerarPDF(data);
      toast.success('Certificado gerado com sucesso!');
    } catch (error) {
      toast.error(error.message || 'Erro ao gerar certificado');
    } finally {
      setGerando(null);
    }
  };

  const gerarPDF = (dados) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Background
    doc.setFillColor(254, 249, 243);
    doc.rect(0, 0, 297, 210, 'F');

    // Border
    doc.setDrawColor(225, 29, 72);
    doc.setLineWidth(3);
    doc.rect(10, 10, 277, 190);
    doc.setLineWidth(0.5);
    doc.rect(15, 15, 267, 180);

    // Header
    doc.setFillColor(225, 29, 72);
    doc.rect(10, 10, 277, 25, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('ENTRAJUDA', 148.5, 27, { align: 'center' });

    // Title
    doc.setTextColor(225, 29, 72);
    doc.setFontSize(32);
    doc.text('CERTIFICADO DE DOAÇÃO', 148.5, 55, { align: 'center' });

    // Subtitle
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(`Ano de ${dados.ano}`, 148.5, 65, { align: 'center' });

    // Main text
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(12);
    const texto = `Certificamos que ${dados.doador?.nome}${dados.doador?.nif ? ` (NIF: ${dados.doador.nif})` : ''} realizou ${dados.resumo?.total_doacoes} doação(ões) durante o ano de ${dados.ano}, contribuindo com um valor estimado total de ${dados.resumo?.valor_total_estimado?.toLocaleString('pt-PT')}€.`;
    
    const linhas = doc.splitTextToSize(texto, 220);
    doc.text(linhas, 148.5, 90, { align: 'center' });

    // Summary box
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(200, 200, 200);
    doc.roundedRect(70, 105, 157, 40, 3, 3, 'FD');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(225, 29, 72);
    doc.text('RESUMO DAS CONTRIBUIÇÕES', 148.5, 115, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    doc.text(`Total de Doações: ${dados.resumo?.total_doacoes}`, 100, 128);
    doc.text(`Valor Total Estimado: ${dados.resumo?.valor_total_estimado?.toLocaleString('pt-PT')}€`, 100, 136);

    // Footer
    doc.setFontSize(10);
    doc.text(`Certificado Nº: ${dados.certificado_id}`, 30, 170);
    doc.text(`Emitido em: ${new Date(dados.emitido_em).toLocaleDateString('pt-PT')}`, 30, 178);

    // Signature
    doc.setFont('helvetica', 'italic');
    doc.text('A Direção da ENTRAJUDA', 230, 175, { align: 'center' });

    // Verification
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Este certificado pode ser verificado em entrajuda.pt/verificar', 148.5, 195, { align: 'center' });

    // Download
    doc.save(`Certificado_Doacao_${dados.ano}_${dados.certificado_id}.pdf`);
  };

  const anos = [];
  const anoAtual = new Date().getFullYear();
  for (let i = anoAtual; i >= anoAtual - 5; i--) {
    anos.push(i.toString());
  }

  return (
    <div className="space-y-6" data-testid="doador-certificados-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Award className="w-7 h-7 text-amber-500" />
          Certificados
        </h1>
        <p className="text-slate-500 mt-1">Gere certificados das suas doações para fins fiscais ou comprovativos</p>
      </div>

      <Tabs defaultValue="gerar">
        <TabsList>
          <TabsTrigger value="gerar">Gerar Novo</TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="gerar" className="space-y-6">
          {/* Certificado Anual */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-rose-500" />
                Certificado Anual
              </CardTitle>
              <CardDescription>
                Gere um certificado com o resumo de todas as suas doações num determinado ano
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Selecione o ano</label>
                  <Select value={anoSelecionado} onValueChange={setAnoSelecionado}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {anos.map(ano => (
                        <SelectItem key={ano} value={ano}>{ano}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={gerarCertificadoAnual}
                  disabled={gerando === 'anual'}
                  className="bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600"
                >
                  {gerando === 'anual' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A gerar...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Gerar Certificado PDF
                    </>
                  )}
                </Button>
              </div>

              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Informação Importante</p>
                    <p className="mt-1">
                      Este certificado serve como comprovativo das suas doações à ENTRAJUDA. 
                      Para efeitos de IRS, consulte o seu contabilista sobre a dedutibilidade fiscal.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Certificados Individuais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Certificados por Doação
              </CardTitle>
              <CardDescription>
                Pode também gerar certificados individuais a partir do seu histórico de doações
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" asChild>
                <a href="/doador/doacoes">
                  Ver Histórico de Doações
                </a>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historico">
          <Card>
            <CardHeader>
              <CardTitle>Certificados Emitidos</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
                </div>
              ) : certificados.length === 0 ? (
                <div className="text-center py-12">
                  <Award className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum certificado emitido ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {certificados.map((cert, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        <div>
                          <p className="font-medium text-slate-900">{cert.certificado_id}</p>
                          <p className="text-sm text-slate-500">
                            {cert.tipo === 'anual' ? `Ano ${cert.ano}` : 'Doação Individual'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-slate-500">
                          {new Date(cert.emitido_em).toLocaleDateString('pt-PT')}
                        </span>
                        <Button variant="outline" size="sm">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
